import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DashboardLayout, Card, ProjectTimelineWidget, NotificationsWidget } from '../components';
import { Breadcrumb } from '../components/Breadcrumb';
import SectionPanel from '../components/SectionPanel';
import TodoList, { type TodoListItem } from '../components/TodoList';
import { KeyFigureList } from '../components/CompanyKeyFigures';
import { ProviderRequestModal, requestPartiesText } from '../components/ProviderRequestDetails';
import { useAuth } from '../context/AuthContext';
import { useProviderRequests, type ProviderType } from '../hooks/useProviderRequests';
import { systemApi, lawyerApi, inspectorApi, projectStatusApi, contractApi, milestoneApi } from '../services/api';
import { type ProjectStatusInstance } from '../services/projectStatusUtils';
import { providerProjectId, providerTodos, type ProviderProject, type ProviderTodo } from '../utils/providerTodos';
import type { KpiTile } from '../utils/kpiTiles';
import type { Contract, Milestone, ProviderRequest, SystemInfo } from '../types';

/**
 * The lawyer's and inspector's start page, with the same modules as the
 * private person's and company's: "Att göra nu", the projects with their
 * timelines, "Dina nyckeltal" and notifications (ClickUp 869fd7bru).
 */
export function ProviderHome({ providerType }: { providerType: ProviderType }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const lawyer = providerType === 'lawyer';
  const base = `/${providerType}/projects`;
  const [systemInfo, setSystemInfo] = useState<SystemInfo | null>(null);
  const [projects, setProjects] = useState<ProviderProject[]>([]);
  const [projectStatusMap, setProjectStatusMap] = useState<Record<string, ProjectStatusInstance>>({});
  const [allContracts, setAllContracts] = useState<Contract[]>([]);
  const [allMilestones, setAllMilestones] = useState<Milestone[]>([]);
  const [answering, setAnswering] = useState<ProviderRequest | null>(null);
  const requests = useProviderRequests(providerType);

  const loadProjects = useCallback(async () => {
    try {
      const loadedProjects: ProviderProject[] = await (lawyer ? lawyerApi : inspectorApi).getMyProjects();
      setProjects(loadedProjects);
      const statusMap: Record<string, ProjectStatusInstance> = {};
      const contractsAll: Contract[] = [];
      // Status instances + contracts per project, the same data the company
      // home loads; the API already scopes them to this provider.
      await Promise.all(loadedProjects.map(async project => {
        try {
          statusMap[project.id] = await projectStatusApi.getInstance(project.id);
        } catch {}
        try {
          contractsAll.push(...await contractApi.getContracts(project.id));
        } catch {}
      }));
      setProjectStatusMap(statusMap);
      setAllContracts(contractsAll);
      const milestonesAll: Milestone[] = [];
      await Promise.all(contractsAll.map(async contract => {
        try {
          milestonesAll.push(...await milestoneApi.getMilestones(contract.id));
        } catch {}
      }));
      setAllMilestones(milestonesAll);
    } catch {}
  }, [lawyer]);

  useEffect(() => {
    systemApi.getSystemInfo().then(setSystemInfo).catch(error => console.error('Failed to load system info:', error));
    void loadProjects();
  }, [loadProjects]);

  const answer = async (action: 'accepted' | 'declined') => {
    if (!answering) return;
    const done = await requests.respond(answering, action);
    if (!done) return;
    setAnswering(null);
    if (action === 'accepted') void loadProjects();
  };

  const todoRow = (todo: ProviderTodo): TodoListItem => {
    if (todo.kind === 'request') {
      const { request } = todo;
      return {
        key: `request-${request.id}`,
        priority: 'waiting',
        icon: 'inbox',
        title: t('providerHome.todo.request.title', { service: request.serviceType || t('providerRequests.fallbackTitle') }),
        // The parties by role, so the Beställare is not read as the contractor's staff (ClickUp 869fk6kz3).
        context: [request.projectDescription, requestPartiesText(request, t) || request.requesterName || request.requesterEmail].filter(Boolean).join(' · '),
        description: t(`providerHome.todo.request.description.${providerType}`),
        actionLabel: t('providerHome.todo.request.action'),
        onAction: () => setAnswering(request),
        testId: `provider-todo-request-${request.id}`
      };
    }
    const projectPath = `${base}/${providerProjectId(todo.project)}`;
    const projectContext = [todo.project.description, todo.project.companyName].filter(Boolean).join(' · ');
    if (todo.kind === 'reviewContract') {
      return {
        key: `contract-${todo.contract.id}`,
        priority: 'waiting',
        icon: 'file-text',
        title: t('providerHome.todo.reviewContract.title', { number: todo.contract.contractNumber }),
        context: projectContext,
        description: t('providerHome.todo.reviewContract.description'),
        actionLabel: t('providerHome.todo.reviewContract.action'),
        href: `${projectPath}?tab=contract`,
        testId: `provider-todo-contract-${todo.contract.id}`
      };
    }
    const description = todo.milestone.description?.trim();
    return {
      key: `stage-${todo.milestone.id}`,
      priority: 'waiting',
      icon: 'clipboard',
      title: t('providerHome.todo.inspectStage.title', {
        stage: description
          ? t('projectView.overview.next.stage', { number: todo.milestone.milestoneNumber, description })
          : t('projectView.overview.next.stageNumber', { number: todo.milestone.milestoneNumber })
      }),
      context: [todo.project.description, t('projectView.overview.contractNumber', { number: todo.contract.contractNumber })]
        .filter(Boolean).join(' · '),
      description: t('providerHome.todo.inspectStage.description'),
      actionLabel: t('providerHome.todo.inspectStage.action'),
      href: projectPath,
      testId: `provider-todo-stage-${todo.milestone.id}`
    };
  };

  const todoRows = providerTodos({
    providerType,
    pendingRequests: requests.pending,
    projects,
    contracts: allContracts,
    milestones: allMilestones
  }).map(todoRow);

  // Same count as "X pågående projekt" in the greeting: started, not completed.
  const ongoing = projects.filter(project => project.status !== 'not_started' && project.status !== 'completed').length;
  const completed = projects.filter(project => project.status === 'completed').length;
  const keyFigures: KpiTile[] = [
    {
      id: 'newRequests', icon: 'inbox', tone: requests.pending.length > 0 ? 'waiting' : 'neutral',
      label: t('providerHome.kpis.newRequests'), value: String(requests.pending.length)
    },
    {
      id: 'ongoing', icon: 'folder', tone: 'neutral',
      label: t(`providerHome.kpis.ongoing.${providerType}`), value: String(ongoing),
      link: { to: base, label: t('providerHome.kpis.show') }
    },
    {
      id: 'completed', icon: 'check-circle', tone: 'done', wide: true,
      label: t(`providerHome.kpis.completed.${providerType}`), value: String(completed),
      link: { to: `${base}/completed`, label: t('providerHome.kpis.show') }
    }
  ];

  const greetingName = user?.fullName || t('home.greeting.fallbackName', 'där');
  const welcome = (systemInfo as Record<string, unknown> | null)?.[lawyer ? 'lawyerinfo' : 'inspectorinfo'] as string | undefined;

  return (
    <DashboardLayout>
      {/* Greeting on a white card: breadcrumb, "Hej {namn}" and the number of ongoing projects. */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="home-greeting">
            <Breadcrumb items={[{ label: t('nav.home') }]} />
            <h3 className="fw-bold mb-1 mt-2 text-break">
              {t('home.greeting.hello', { name: greetingName, defaultValue: 'Hej {{name}}' })}
            </h3>
            <p className="text-muted mb-0">
              {t('home.greeting.ongoingProjects', { count: ongoing, defaultValue: '{{count}} pågående projekt' })}
            </p>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-lg-8">
          <SectionPanel
            title={t('home.todoNow.title')}
            icon="clock"
            className="mb-4"
            fillHeight={false}
            headerAction={todoRows.length > 0 ? (
              <span className="badge bg-danger flex-shrink-0">{t('home.todoNow.count', { count: todoRows.length })}</span>
            ) : undefined}
          >
            {requests.error && !answering && <div className="alert alert-danger mb-3">{requests.error}</div>}
            <TodoList items={todoRows} emptyTestId="provider-todo-empty" />
          </SectionPanel>

          {/* Shared with the company and private-person start pages (read-only). */}
          <ProjectTimelineWidget
            className="card mb-4"
            title={t(`providerHome.projectsTitle.${providerType}`)}
            projects={projects}
            contracts={allContracts}
            milestones={allMilestones}
            statusSteps={Object.fromEntries(
              Object.entries(projectStatusMap).map(([id, instance]) => [id, instance?.steps || []])
            )}
            projectHref={project => `${base}/${providerProjectId(project as ProviderProject)}`}
            showCompanyBadge
          />
        </div>

        <div className="col-lg-4">
          <SectionPanel title={t('home.kpis.title')} icon="bar-chart-2" className="mb-4" fillHeight={false}>
            <KeyFigureList tiles={keyFigures} testId="provider-key-figures" />
          </SectionPanel>

          <NotificationsWidget />

          {welcome && (
            <Card title={t('home.welcome.title')} icon="home">
              <div className="ql-editor p-0" dangerouslySetInnerHTML={{ __html: welcome }} />
            </Card>
          )}
          {systemInfo?.extraimportant && (
            <Card title={t('home.importantInfo.title')} icon="alert-circle">
              <div className="ql-editor p-0" dangerouslySetInnerHTML={{ __html: systemInfo.extraimportant }} />
              {systemInfo.updatedAt && (
                <div className="mt-3 pt-2 border-top">
                  <small className="text-muted">
                    <i className="feather-clock me-1" aria-hidden="true"></i>
                    {t('home.importantInfo.updatedAt')}: {new Date(systemInfo.updatedAt).toLocaleDateString('sv-SE', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </small>
                </div>
              )}
            </Card>
          )}
        </div>
      </div>

      {answering && (
        <ProviderRequestModal
          request={answering}
          busy={requests.busyId === answering.id}
          error={requests.error}
          onRespond={action => { void answer(action); }}
          onClose={() => setAnswering(null)}
        />
      )}
    </DashboardLayout>
  );
}
